// Ladies Aura — product page behaviour (templates/product.json).
// No dependencies. Every CTA clicks the theme's real Add to cart button, so
// variant selection, quantity, the cart drawer and analytics keep working.
(() => {
  if (window.__laPdp) return;
  window.__laPdp = true;
  document.documentElement.classList.add('la-js');

  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const ready = (fn) => (document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', fn) : fn());

  ready(() => {
    const realButton = document.querySelector(
      '.product-information form[action*="/cart/add"] button[name="add"], .product-information form[action*="/cart/add"] button[type="submit"]'
    );

    /* ---------- Reveal on scroll ---------- */
    const revealEls = document.querySelectorAll('[data-la-reveal]');
    if (reduce || !('IntersectionObserver' in window)) {
      revealEls.forEach((el) => el.classList.add('is-in'));
    } else {
      const io = new IntersectionObserver(
        (entries) => entries.forEach((e) => {
          if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
        }),
        { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
      );
      revealEls.forEach((el) => io.observe(el));
    }

    /* ---------- CTAs → real Add to cart ---------- */
    const priceText = () => {
      const el = document.querySelector('.product-information product-price .price, .product-information .price');
      const m = el && el.textContent.match(/[$€£]\s?\d[\d.,]*/);
      return m ? m[0] : null;
    };
    const syncCtas = () => {
      const p = priceText();
      if (p) document.querySelectorAll('[data-la-live-price], [data-la-sticky-price]').forEach((el) => { el.textContent = p; });
      if (!realButton) return;
      const off = realButton.disabled || realButton.getAttribute('aria-disabled') === 'true';
      document.querySelectorAll('[data-la-atc]').forEach((b) => {
        b.disabled = off;
        const label = b.querySelector('span') || b;
        label.textContent = off ? 'Sold out' : 'Add to cart';
      });
    };
    document.addEventListener('click', (e) => {
      const b = e.target.closest('[data-la-atc]');
      if (!b) return;
      if (realButton && !realButton.disabled) realButton.click();
      else document.querySelector('.product-information')?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth' });
    });
    document.addEventListener('variant:update', () => setTimeout(syncCtas, 0));
    realButton?.form?.addEventListener('change', () => setTimeout(syncCtas, 60));
    syncCtas();

    /* ---------- Sticky add-to-cart (mobile) ---------- */
    const bar = document.querySelector('[data-la-sticky]');
    if (bar && realButton && 'IntersectionObserver' in window) {
      document.body.appendChild(bar);
      let buttonVisible = true;
      let footerVisible = false;
      const update = () => {
        const show = !buttonVisible && !footerVisible;
        bar.dataset.visible = String(show);
        bar.setAttribute('aria-hidden', String(!show));
        bar.querySelector('button').tabIndex = show ? 0 : -1;
        document.body.classList.toggle('la-sticky-on', show);
      };
      new IntersectionObserver(([e]) => { buttonVisible = e.isIntersecting; update(); }).observe(realButton);
      const footer = document.querySelector('footer, .footer, #shopify-section-footer, [id*="footer-group"]');
      if (footer) new IntersectionObserver(([e]) => { footerVisible = e.isIntersecting; document.body.classList.toggle('la-footer-on', footerVisible); update(); }).observe(footer);
    } else if (bar) {
      bar.remove();
    }

    /* ---------- How it works: progress rail ---------- */
    const steps = document.querySelector('[data-la-steps]');
    if (steps && !reduce) {
      let ticking = false;
      const paint = () => {
        const r = steps.getBoundingClientRect();
        const start = innerHeight * 0.85;
        const p = Math.min(1, Math.max(0, (start - r.top) / (r.height + start * 0.25)));
        steps.style.setProperty('--p', p.toFixed(3));
        ticking = false;
      };
      addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(paint); } }, { passive: true });
      paint();
    } else if (steps) {
      steps.style.setProperty('--p', 1);
    }

    /* ---------- Product education tabs ---------- */
    document.querySelectorAll('[data-la-tabs]').forEach((root) => {
      const tabs = [...root.querySelectorAll('[role="tab"]')];
      const scan = root.querySelector('.la-tech__scan');
      const select = (tab, focus) => {
        tabs.forEach((t) => {
          const on = t === tab;
          t.setAttribute('aria-selected', String(on));
          t.tabIndex = on ? 0 : -1;
          const panel = document.getElementById(t.getAttribute('aria-controls'));
          if (panel) panel.hidden = !on;
        });
        const id = tab.id.replace('la-tab-', '');
        root.querySelectorAll('.la-tech__img').forEach((img) => img.classList.toggle('is-on', img.dataset.for === id));
        if (scan && !reduce) { scan.classList.remove('is-run'); void scan.offsetWidth; scan.classList.add('is-run'); }
        if (focus) tab.focus();
        const strip = tab.parentElement;
        if (strip.scrollWidth > strip.clientWidth) strip.scrollTo({ left: tab.offsetLeft - 16, behavior: reduce ? 'auto' : 'smooth' });
      };
      tabs.forEach((t, i) => {
        t.addEventListener('click', () => select(t));
        t.addEventListener('keydown', (e) => {
          const k = e.key;
          if (k !== 'ArrowRight' && k !== 'ArrowLeft' && k !== 'Home' && k !== 'End') return;
          e.preventDefault();
          const n = k === 'Home' ? 0 : k === 'End' ? tabs.length - 1 : (i + (k === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
          select(tabs[n], true);
        });
      });
    });

    /* ---------- Video: lazy load, autoplay muted in view, play/pause ---------- */
    document.querySelectorAll('[data-la-video]').forEach((frame) => {
      const v = frame.querySelector('video');
      const btn = frame.querySelector('[data-la-video-toggle]');
      const progress = frame.querySelector('.la-video__bar');
      let userPaused = reduce;
      const load = () => { if (!v.src && v.dataset.src) v.src = v.dataset.src; };
      const play = () => { load(); v.play().catch(() => {}); };
      v.addEventListener('play', () => { frame.classList.add('is-playing'); btn.setAttribute('aria-label', 'Pause video'); });
      v.addEventListener('pause', () => { frame.classList.remove('is-playing'); btn.setAttribute('aria-label', 'Play video'); });
      v.addEventListener('timeupdate', () => { if (progress && v.duration) progress.style.setProperty('--p', (v.currentTime / v.duration).toFixed(3)); });
      btn.addEventListener('click', () => {
        if (v.paused) { userPaused = false; play(); } else { userPaused = true; v.pause(); }
      });
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(([e]) => {
          if (e.isIntersecting) { load(); if (!userPaused) play(); } else if (!v.paused) v.pause();
        }, { threshold: 0.5 }).observe(frame);
      }
    });

    /* ---------- Cross-sell quick add ---------- */
    document.querySelectorAll('form.la-cross__form').forEach((form) => {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const btn = form.querySelector('button');
        btn.disabled = true;
        try {
          const res = await fetch('/cart/add.js', { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
          if (!res.ok) throw new Error('add failed');
          const cart = await (await fetch('/cart.js')).json();
          document.querySelector('cart-icon')?.renderCartBubble?.(cart.item_count);
          btn.classList.add('is-added');
          btn.setAttribute('aria-label', 'Added to cart');
          btn.innerHTML = '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m5 12.5 4.5 4.5L19 7.5"/></svg>';
        } catch (_) {
          form.submit();
        }
      });
    });
  });
})();
